/**
 * Zentraler Normalisierungs-Adapter für Arbeitgeber-Gesamtkosten (totalPersonnelCost)
 * und Mitarbeiter-Sollumsatz-Basis (Deutschland Brutto vs. Südtirol/IT_BZ Netto-Multiplikator).
 */

export type PersonnelCountryMode = 'DE' | 'IT_BZ' | 'AT';

export interface PersonnelCostEmployeeInput {
    id?: string;
    name?: string;
    country?: PersonnelCountryMode | string;
    kategorie?: 'produktiv' | 'unproduktiv' | 'azubi' | string;
    taetigkeit?: string;
    wochenstunden?: number | string;
    grossWage?: number | string;
    grundgehalt?: number | string;
    lohnzusatz?: number | string;
    lohnkostenzusatz?: number | string;
    bonus?: number | string;
    netWage?: number | string;
    costoAziendaleOverride?: number | string;
    linkedProductiveEmployeeId?: string;
    isSplitDeduction?: boolean;
    /** Interner Referenzwert für Schwellenprüfung (Minijob/Vollzeit) vor Split-Abzug */
    _contractGrossBase?: number;
    _contractWochenstunden?: number;
}

export interface PersonnelCostSettingsContext {
    selectedCountryId?: string;
    clientLand?: string;
    countrySettings?: {
        Lohnnebenkosten?: number;
        lohnnebenkosten?: number;
        Lohnnebenkosten_Minijob?: number;
        lohnnebenkosten_minijob?: number;
        Minijob_Lohn_Grenze?: number;
        ItBz_Faktor_Azubi?: number;
        ItBz_Faktor_Teilzeit?: number;
        ItBz_Faktor_Vollzeit?: number;
        ItBz_Vollzeit_Stundenschwelle?: number;
        At_Lohnnebenkosten_Fachkraft?: number;
        At_Lohnnebenkosten_Azubi?: number;
        [key: string]: any;
    };
}

export interface NormalizedPersonnelCostResult {
    isItBz: boolean;
    effectiveCountry: 'DE' | 'AT' | 'CH' | 'IT_BZ';
    /** Basis-Lohn (DE: Brutto-Grundgehalt, IT_BZ: Netto in busta) */
    baseWage: number;
    lohnzusatz: number;
    bonus: number;
    /** Effektiver Lohn ohne Bonus (Grundlohn + Lohnzusatz) */
    grundlohn: number;
    /** Gesamtlohn inkl. Bonus (Basis für Stundenlohn Gesamt & Planumsatz) */
    gesamtlohnBasis: number;
    /** Effektiver AG-Aufschlag in % (für SV-% Spalte & Durchschnitts-Anzeige) */
    lohnnebenkostenSatz: number;
    /** AG-Lohnnebenkosten in EUR (totalPersonnelCost - gesamtlohnBasis) */
    lohnnebenkosten: number;
    /** Echte Arbeitgeber-Gesamtkosten (Costo Totale Aziendale) */
    totalPersonnelCost: number;
    /** Verwendeter IT_BZ Multiplikator (1.42 / 1.65 / 1.80) oder Override-Faktor */
    appliedMultiplier: number;
    isOverrideActive: boolean;
}

const parseNum = (val: unknown): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
    if (typeof val === 'string') {
        const clean = val.replace(/\./g, '').replace(',', '.').trim();
        const num = parseFloat(clean);
        return Number.isFinite(num) ? num : 0;
    }
    return 0;
};

/**
 * Prüft, ob für den Mitarbeiter bzw. Mandanten das Südtiroler (IT_BZ) Netto-Modell aktiv ist.
 */
export function isItBzMode(
    employee?: PersonnelCostEmployeeInput | null,
    ctx?: PersonnelCostSettingsContext
): boolean {
    const empCountry = String(employee?.country || '').toUpperCase().trim();
    if (empCountry === 'IT_BZ' || empCountry === 'IT') return true;
    if (empCountry === 'DE' || empCountry === 'AT' || empCountry === 'CH') return false;

    const rawLand = String(ctx?.clientLand || ctx?.selectedCountryId || '')
        .toUpperCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    return (
        rawLand === 'IT' ||
        rawLand === 'IT_BZ' ||
        rawLand.includes('ITALIEN') ||
        rawLand.includes('ITALY') ||
        rawLand.includes('SUEDTIROL') ||
        rawLand.includes('SUDTIROL') ||
        rawLand.includes('SOUTH TYROL') ||
        rawLand.includes('ALTO ADIGE')
    );
}

/**
 * Prüft, ob für den Mitarbeiter bzw. Mandanten das österreichische (AT) KV-Brutto-Modell aktiv ist.
 */
export function isAtMode(
    employee?: PersonnelCostEmployeeInput | null,
    ctx?: PersonnelCostSettingsContext
): boolean {
    const empCountry = String(employee?.country || '').toUpperCase().trim();
    if (empCountry === 'AT') return true;
    if (empCountry === 'DE' || empCountry === 'IT_BZ' || empCountry === 'IT' || empCountry === 'CH') return false;

    const rawLand = String(ctx?.clientLand || ctx?.selectedCountryId || '')
        .toUpperCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    return rawLand === 'AT' || rawLand.includes('OSTERREICH') || rawLand.includes('AUSTRIA');
}

/**
 * Prüft, ob ein Mitarbeiter als Azubi / Lehrling eingestuft ist.
 * WICHTIG: Eine explizit gewählte "Assistenz" / "Assistenzkraft" ist KEIN Azubi
 * (auch dann nicht, wenn sie in der Tabelle "Azubis & Assistenz" steht).
 */
export function isAzubiEmployee(
    employee?: PersonnelCostEmployeeInput | null,
    tableScope?: 'produktiv' | 'unproduktiv' | 'azubi'
): boolean {
    if (!employee) return false;
    const role = String(employee.taetigkeit || '').trim();
    const roleLower = role.toLowerCase();

    // Assistenz / Assistenzkraft ist niemals ein Lehrling/Azubi
    if (roleLower === 'assistenz' || roleLower === 'assistenzkraft') {
        return false;
    }

    const kat = String(employee.kategorie || '').toLowerCase().trim();
    if (kat === 'azubi') return true;

    if (['Azubi', 'Prod. Azubi', 'produktiver Azubi'].includes(role)) {
        return true;
    }

    // In der Tabelle "Azubis & Assistenz" gilt Azubi nur, solange nicht Assistenz gewählt wurde
    if (tableScope === 'azubi') {
        return true;
    }

    return false;
}

/**
 * Kern-Funktion laut Master-Briefing:
 * Berechnet die echten Arbeitgeber-Gesamtkosten (totalPersonnelCost / Costo Totale Aziendale).
 *
 * - Prio 1 (StB-Override): costoAziendaleOverride > 0 -> Override-Wert
 * - Prio 2 (IT_BZ): Azubi * 1.42, Teilzeit (<30h) * 1.65, Vollzeit (>=30h) * 1.80 (inkl. Settings-Fallbacks)
 * - Prio 3 (DE / Standard): grossWage * (1 + Lohnnebenkosten% / 100)
 */
export function calculateTotalPersonnelCost(
    employee: PersonnelCostEmployeeInput,
    ctx?: PersonnelCostSettingsContext
): number {
    return resolvePersonnelCostMetrics(employee, ctx).totalPersonnelCost;
}

/**
 * Liefert das vollständige, normalisierte Kosten- und Lohn-Paket für Selektoren & UI.
 */
export function resolvePersonnelCostMetrics(
    employee: PersonnelCostEmployeeInput,
    ctx?: PersonnelCostSettingsContext,
    tableScope: 'produktiv' | 'unproduktiv' | 'azubi' = 'produktiv'
): NormalizedPersonnelCostResult {
    const itBz = isItBzMode(employee, ctx);
    const countrySettings = ctx?.countrySettings || {};
    const wochenstunden = parseNum(employee?.wochenstunden);
    const lohnzusatz = parseNum(employee?.lohnkostenzusatz ?? employee?.lohnzusatz);
    const bonus = parseNum(employee?.bonus);
    const overrideVal = parseNum(employee?.costoAziendaleOverride);

    if (itBz) {
        // In Südtirol (IT_BZ) ist das vereinbarte Netto ("Netto in busta") die Basis.
        // Fallback auf grundgehalt/grossWage, falls in der bestehenden Spalte eingetragen.
        const netBase = parseNum(
            employee?.netWage !== undefined && employee?.netWage !== null && employee?.netWage !== ''
                ? employee.netWage
                : (employee?.grundgehalt ?? employee?.grossWage)
        );
        const grundlohn = netBase + lohnzusatz;
        const gesamtlohnBasis = grundlohn + bonus;

        const azubiFactor = Number(countrySettings.ItBz_Faktor_Azubi) > 0 ? Number(countrySettings.ItBz_Faktor_Azubi) : 1.42;
        const partTimeFactor = Number(countrySettings.ItBz_Faktor_Teilzeit) > 0 ? Number(countrySettings.ItBz_Faktor_Teilzeit) : 1.65;
        const fullTimeFactor = Number(countrySettings.ItBz_Faktor_Vollzeit) > 0 ? Number(countrySettings.ItBz_Faktor_Vollzeit) : 1.80;
        const fullTimeThreshold = Number(countrySettings?.ItBz_Vollzeit_Stundenschwelle) || 30;

        const isAzubi = isAzubiEmployee(employee, tableScope);
        const thresholdHours =
            employee?._contractWochenstunden !== undefined && Number.isFinite(employee._contractWochenstunden)
                ? employee._contractWochenstunden
                : wochenstunden;
        const stepMultiplier = isAzubi
            ? azubiFactor
            : thresholdHours < fullTimeThreshold
                ? partTimeFactor
                : fullTimeFactor;

        const isOverrideActive = overrideVal > 0;
        // Netto-Basis inkl. Bonus/Zusatz für Stufen-Multiplikation
        const netTotalForCalc = netBase + lohnzusatz + bonus;
        const totalPersonnelCost = isOverrideActive
            ? overrideVal
            : netTotalForCalc * stepMultiplier;

        const appliedMultiplier = isOverrideActive
            ? (netTotalForCalc > 0 ? totalPersonnelCost / netTotalForCalc : stepMultiplier)
            : stepMultiplier;

        const lohnnebenkosten = Math.max(0, totalPersonnelCost - gesamtlohnBasis);
        const lohnnebenkostenSatz = netTotalForCalc > 0
            ? Math.max(0, (appliedMultiplier - 1) * 100)
            : Math.round((stepMultiplier - 1) * 1000) / 10;

        return {
            isItBz: true,
            effectiveCountry: 'IT_BZ',
            baseWage: netBase,
            lohnzusatz,
            bonus,
            grundlohn,
            gesamtlohnBasis,
            lohnnebenkostenSatz,
            lohnnebenkosten,
            totalPersonnelCost,
            appliedMultiplier,
            isOverrideActive,
        };
    }

    // --- DE / AT / CH Standard-Systematik (Brutto-Basis) ---
    const atMode = isAtMode(employee, ctx);
    const rawLand = String(ctx?.clientLand || ctx?.selectedCountryId || 'DE')
        .toUpperCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const effectiveCountry: 'DE' | 'AT' | 'CH' =
        atMode || rawLand === 'AT' || rawLand.includes('OSTERREICH') || rawLand.includes('AUSTRIA')
            ? 'AT'
            : rawLand === 'CH' || rawLand.includes('SCHWEIZ') || rawLand.includes('SWITZERLAND')
                ? 'CH'
                : 'DE';

    const grossBase = parseNum(
        employee?.grossWage !== undefined && employee?.grossWage !== null && employee?.grossWage !== ''
            ? employee.grossWage
            : employee?.grundgehalt
    );
    const grundlohn = grossBase + lohnzusatz;
    const gesamtlohnBasis = grundlohn + bonus;

    if (effectiveCountry === 'AT') {
        const atGeringGrenze = Number(countrySettings?.Minijob_Lohn_Grenze) || 518.44;
        const isAzubi = isAzubiEmployee(employee, tableScope);
        const role = String(employee?.taetigkeit || '').trim();
        const rawAzubiPct = countrySettings?.At_Lohnnebenkosten_Azubi;
        const rawGeringPct = countrySettings?.Lohnnebenkosten_Minijob ?? countrySettings?.lohnnebenkosten_minijob;
        const rawFachkraftPct = countrySettings?.At_Lohnnebenkosten_Fachkraft;

        let percent: number;
        const thresholdBasisAt =
            employee?._contractGrossBase !== undefined && Number.isFinite(employee._contractGrossBase) && employee._contractGrossBase > 0
                ? employee._contractGrossBase
                : gesamtlohnBasis;
        if (isAzubi) {
            // Prio 1: Azubi / Lehrling — immer 35.0 % (auch unterhalb der Geringfügigkeitsgrenze)
            percent = rawAzubiPct !== undefined && rawAzubiPct !== null && Number(rawAzubiPct) > 0
                ? Number(rawAzubiPct)
                : 35.0;
        } else if (thresholdBasisAt > 0 && thresholdBasisAt <= atGeringGrenze && role !== 'Unprod. Anteil') {
            // Prio 2: Geringfügig Beschäftigte (Nicht-Azubi, z. B. Assistenz/Reinigung <= 518,44 €)
            const gNum = Number(rawGeringPct);
            percent = rawGeringPct !== undefined && rawGeringPct !== null && gNum > 0 && gNum !== 30.5 && gNum !== 52.0
                ? gNum
                : 16.4;
        } else {
            // Prio 3: Reguläre Fachkraft / Assistenz über Geringfügigkeitsgrenze
            percent = rawFachkraftPct !== undefined && rawFachkraftPct !== null && Number(rawFachkraftPct) > 0
                ? Number(rawFachkraftPct)
                : 52.0;
        }

        if (overrideVal > 0) {
            const lohnnebenkosten = Math.max(0, overrideVal - gesamtlohnBasis);
            const effSatz = gesamtlohnBasis > 0 ? (lohnnebenkosten / gesamtlohnBasis) * 100 : percent;
            return {
                isItBz: false,
                effectiveCountry: 'AT',
                baseWage: grossBase,
                lohnzusatz,
                bonus,
                grundlohn,
                gesamtlohnBasis,
                lohnnebenkostenSatz: effSatz,
                lohnnebenkosten,
                totalPersonnelCost: overrideVal,
                appliedMultiplier: 1 + effSatz / 100,
                isOverrideActive: true,
            };
        }

        const totalPersonnelCost = gesamtlohnBasis * (1 + (percent / 100));
        const lohnnebenkosten = totalPersonnelCost - gesamtlohnBasis;

        return {
            isItBz: false,
            effectiveCountry: 'AT',
            baseWage: grossBase,
            lohnzusatz,
            bonus,
            grundlohn,
            gesamtlohnBasis,
            lohnnebenkostenSatz: percent,
            lohnnebenkosten,
            totalPersonnelCost,
            appliedMultiplier: 1 + (percent / 100),
            isOverrideActive: false,
        };
    }

    const dbLnk = countrySettings?.Lohnnebenkosten ?? countrySettings?.lohnnebenkosten;
    const defaultLnk = effectiveCountry === 'CH' ? 17.0 : effectiveCountry === 'AT' ? 52.0 : 22.5;
    let lohnnebenkostenSatz = dbLnk !== undefined && dbLnk !== null ? Number(dbLnk) : defaultLnk;

    const minijobGrenze = Number(countrySettings?.Minijob_Lohn_Grenze) || 602;
    const role = String(employee?.taetigkeit || '').trim();
    const roleLower = role.toLowerCase();
    const isAssistenzRole = roleLower === 'assistenz' || roleLower === 'assistenzkraft';
    const isRealAzubi = !isAssistenzRole && isAzubiEmployee(employee, tableScope);

    const thresholdGrossDe =
        employee?._contractGrossBase !== undefined && Number.isFinite(employee._contractGrossBase) && employee._contractGrossBase > 0
            ? employee._contractGrossBase
            : grossBase;
    if (effectiveCountry === 'DE' && thresholdGrossDe > 0 && thresholdGrossDe <= minijobGrenze) {
        if (!isRealAzubi && role !== 'Unprod. Anteil') {
            lohnnebenkostenSatz = Number(
                countrySettings?.Lohnnebenkosten_Minijob ?? countrySettings?.lohnnebenkosten_minijob ?? 30.0
            );
        }
    }

    if (overrideVal > 0) {
        const lohnnebenkosten = Math.max(0, overrideVal - gesamtlohnBasis);
        const effSatz = gesamtlohnBasis > 0 ? (lohnnebenkosten / gesamtlohnBasis) * 100 : lohnnebenkostenSatz;
        return {
            isItBz: false,
            effectiveCountry,
            baseWage: grossBase,
            lohnzusatz,
            bonus,
            grundlohn,
            gesamtlohnBasis,
            lohnnebenkostenSatz: effSatz,
            lohnnebenkosten,
            totalPersonnelCost: overrideVal,
            appliedMultiplier: 1 + effSatz / 100,
            isOverrideActive: true,
        };
    }

    const lohnnebenkosten = (grundlohn + bonus) * (lohnnebenkostenSatz / 100);
    const totalPersonnelCost = grundlohn + lohnnebenkosten + bonus;

    return {
        isItBz: false,
        effectiveCountry,
        baseWage: grossBase,
        lohnzusatz,
        bonus,
        grundlohn,
        gesamtlohnBasis,
        lohnnebenkostenSatz,
        lohnnebenkosten,
        totalPersonnelCost,
        appliedMultiplier: 1 + lohnnebenkostenSatz / 100,
        isOverrideActive: false,
    };
}

/**
 * Liefert die maßgebliche Lohn-Basis eines Mitarbeiters für die Multiplikation mit dem Lohnfaktor
 * (Sollumsatz / Kostendeckungsumsatz).
 * - IT_BZ: Vereinbartes Netto ("Netto in busta") + etwaige Zuschläge
 * - DE/AT/CH: Vereinbartes Brutto-Grundgehalt + etwaige Zuschläge
 */
export function resolveEmployeeEffectiveWageBasis(
    employee: PersonnelCostEmployeeInput,
    ctx?: PersonnelCostSettingsContext
): number {
    const itBz = isItBzMode(employee, ctx);
    const lohnzusatz = parseNum(employee?.lohnkostenzusatz ?? employee?.lohnzusatz);
    if (itBz) {
        const netBase = parseNum(
            employee?.netWage !== undefined && employee?.netWage !== null && employee?.netWage !== ''
                ? employee.netWage
                : (employee?.grundgehalt ?? employee?.grossWage)
        );
        return netBase + lohnzusatz;
    }
    const grossBase = parseNum(
        employee?.grossWage !== undefined && employee?.grossWage !== null && employee?.grossWage !== ''
            ? employee.grossWage
            : employee?.grundgehalt
    );
    return grossBase + lohnzusatz;
}

/**
 * Verrechnet für Zeilen in `unproductivePersonalData` mit `taetigkeit === 'Unprod. Anteil'`,
 * `linkedProductiveEmployeeId` und `isSplitDeduction === true` synchron den Abzug von Gehalt
 * und Wochenstunden beim verknüpften produktiven Mitarbeiter in `personalData`.
 *
 * - Verhindert Überbuchung über das ursprüngliche Gehalt / die ursprünglichen Wochenstunden hinaus.
 * - Bewahrt `_contractGrossBase` und `_contractWochenstunden` zur korrekten Nebenkosten-Einstufung
 *   (keine Doppelzählung der Arbeitgeber-Nebenkosten).
 */
export function applyUnproductiveSplitToPersonalLists<TProd extends Record<string, any>, TUnprod extends Record<string, any>>(
    rawPersonalData: TProd[] | undefined | null,
    rawUnproductiveData: TUnprod[] | undefined | null
): {
    effectivePersonalData: TProd[];
    effectiveUnproductiveData: TUnprod[];
} {
    const pList = Array.isArray(rawPersonalData) ? rawPersonalData : [];
    const uList = Array.isArray(rawUnproductiveData) ? rawUnproductiveData : [];

    // Tracking pro produktivem Mitarbeiter (id -> verfügbares Restbudget & kumulierter Abzug)
    const prodBudgetMap = new Map<
        string,
        {
            rawGrundgehalt: number;
            rawNetWage: number;
            rawWochenstunden: number;
            deductedGrundgehalt: number;
            deductedWochenstunden: number;
        }
    >();

    for (const ma of pList) {
        const id = String(ma?.id || '').trim();
        if (!id) continue;
        const rawGrund = Math.max(0, parseNum(ma?.grundgehalt));
        const rawNet = Math.max(0, parseNum(ma?.netWage));
        const rawHours = Math.max(0, parseNum(ma?.wochenstunden));
        prodBudgetMap.set(id, {
            rawGrundgehalt: rawGrund,
            rawNetWage: rawNet,
            rawWochenstunden: rawHours,
            deductedGrundgehalt: 0,
            deductedWochenstunden: 0,
        });
    }

    const effectiveUnproductiveData = uList.map((uRow) => {
        if (!uRow || typeof uRow !== 'object') return uRow;
        const isUnprodAnteil = String(uRow.taetigkeit || '').trim() === 'Unprod. Anteil';
        if (!isUnprodAnteil) {
            return uRow;
        }

        const rawLinkedId = String(uRow.linkedProductiveEmployeeId || '').trim();
        const linkedProd =
            (rawLinkedId ? pList.find((p) => String(p?.id || '').trim() === rawLinkedId) : undefined) ||
            (uRow.name
                ? pList.find((p) => String(p?.name || '').trim() === String(uRow.name || '').trim())
                : undefined);
        const linkedId = String(linkedProd?.id || '').trim();
        const budget = linkedId ? prodBudgetMap.get(linkedId) : undefined;
        if (!linkedProd || !budget) {
            return uRow;
        }

        // Synchronisiere den Namen mit dem verknüpften produktiven Mitarbeiter, falls vorhanden
        const syncedName = linkedProd.name ? String(linkedProd.name) : uRow.name;
        // Bei 'Unprod. Anteil' mit verknüpftem Mitarbeiter ist Split-Abzug standardmäßig aktiv (sofern nicht explizit false)
        const isSplit = uRow.isSplitDeduction !== false;

        if (!isSplit) {
            // Modus 1: Zusatzvergütung (kein Abzug oben beim produktiven MA)
            return {
                ...uRow,
                name: syncedName,
                _contractGrossBase: budget.rawGrundgehalt,
                _contractWochenstunden: budget.rawWochenstunden,
            };
        }

        // Modus 2: Split-Abzug / Transfer (gedeckelt auf verfügbares Gehalt / Stunden oben)
        const availWage = Math.max(0, budget.rawGrundgehalt - budget.deductedGrundgehalt);
        const availHours = Math.max(0, budget.rawWochenstunden - budget.deductedWochenstunden);

        const reqWage = Math.max(0, parseNum(uRow.grundgehalt));
        const reqHours = Math.max(0, parseNum(uRow.wochenstunden));

        const clampedWage = Math.min(reqWage, availWage);
        const clampedHours = Math.min(reqHours, availHours);

        budget.deductedGrundgehalt += clampedWage;
        budget.deductedWochenstunden += clampedHours;

        const hasNetWage = uRow.netWage !== undefined && uRow.netWage !== null && uRow.netWage !== '';

        // Falls der produktive MA einen StB-Costo-Override hat, anteilig aufteilen (verhindert Doppelzählung)
        const prodOverride = parseNum(linkedProd.costoAziendaleOverride);
        const splitOverride =
            prodOverride > 0 && budget.rawGrundgehalt > 0
                ? prodOverride * (clampedWage / budget.rawGrundgehalt)
                : undefined;

        return {
            ...uRow,
            name: syncedName,
            grundgehalt: clampedWage,
            ...(hasNetWage ? { netWage: Math.min(Math.max(0, parseNum(uRow.netWage)), availWage) } : {}),
            wochenstunden: clampedHours,
            ...(splitOverride !== undefined ? { costoAziendaleOverride: splitOverride } : {}),
            _contractGrossBase: budget.rawGrundgehalt,
            _contractWochenstunden: budget.rawWochenstunden,
        };
    });

    const effectivePersonalData = pList.map((ma) => {
        if (!ma || typeof ma !== 'object') return ma;
        const id = String(ma?.id || '').trim();
        const budget = id ? prodBudgetMap.get(id) : undefined;
        if (!budget || (budget.deductedGrundgehalt <= 0 && budget.deductedWochenstunden <= 0)) {
            return {
                ...ma,
                rawGrundgehalt: Math.max(0, parseNum(ma?.grundgehalt)),
                rawWochenstunden: Math.max(0, parseNum(ma?.wochenstunden)),
                splitDeductedGrundgehalt: 0,
                splitDeductedWochenstunden: 0,
            };
        }

        const restGrundgehalt = Math.max(0, budget.rawGrundgehalt - budget.deductedGrundgehalt);
        const restWochenstunden = Math.max(0, budget.rawWochenstunden - budget.deductedWochenstunden);
        const hasNetWage = ma.netWage !== undefined && ma.netWage !== null && ma.netWage !== '';
        const restNetWage = hasNetWage ? Math.max(0, budget.rawNetWage - budget.deductedGrundgehalt) : ma.netWage;

        const prodOverride = parseNum(ma.costoAziendaleOverride);
        const restOverride =
            prodOverride > 0 && budget.rawGrundgehalt > 0
                ? prodOverride * (restGrundgehalt / budget.rawGrundgehalt)
                : ma.costoAziendaleOverride;

        return {
            ...ma,
            grundgehalt: restGrundgehalt,
            ...(hasNetWage ? { netWage: restNetWage } : {}),
            wochenstunden: restWochenstunden,
            ...(prodOverride > 0 ? { costoAziendaleOverride: restOverride } : {}),
            rawGrundgehalt: budget.rawGrundgehalt,
            rawWochenstunden: budget.rawWochenstunden,
            splitDeductedGrundgehalt: budget.deductedGrundgehalt,
            splitDeductedWochenstunden: budget.deductedWochenstunden,
            _contractGrossBase: budget.rawGrundgehalt,
            _contractWochenstunden: budget.rawWochenstunden,
        };
    });

    return {
        effectivePersonalData,
        effectiveUnproductiveData,
    };
}

